import moment from 'moment';
import {useHistory} from 'react-router-dom';
import {
  Avatar,
  Card,
  CardContent,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';
import {AffiliatedUser} from '../../../backend/responses/affiliated-user';

const cardSx: SxProps<Theme> = {
  borderRadius: '5px',
  background: 'var(--dcm-white-base)',
  boxShadow: '0px -3px 14px 0px rgba(29, 41, 57, 0.10)',
  width: '146px',
};

const cardContentSx: SxProps<Theme> = {
  padding: '16px!important',
};

let avatarSx: SxProps<Theme> = {
  width: '64px',
  height: '64px',
};

const usernameSx: SxProps<Theme> = {
  color: 'var(--dcm-gray)',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '18px',
  maxWidth: `calc(146px - ${2 * 16}px)`,
};

const taskConclusionSx: SxProps<Theme> = {
  color: 'var(--dcm-red-base)',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '18px',
  textAlign: 'center',
};

export const EntityUserCardSkeleton = () => {
  return (
    <Card sx={cardSx}>
      <CardContent sx={cardContentSx}>
        <Stack gap="14px" justifyContent="center" alignItems="center">
          <Skeleton variant="circular" sx={avatarSx} />
          <Skeleton variant="rounded" sx={{height: '18px', width: '100px'}} />
          <Skeleton variant="rounded" sx={{height: '18px', width: '80px'}} />
        </Stack>
      </CardContent>
    </Card>
  );
};

export const EntityUserCard = ({
  data,
  toLink,
  columns,
}: {
  data: AffiliatedUser;
  toLink: string;
  columns?: [{columnId: 'tasks' | string; value: CareTaskComplementary[] | undefined}] | any;
}) => {
  const {t} = useTranslation();

  const history = useHistory();

  const userTasks = columns[0].value as CareTaskComplementary[];

  const handleCardClick = () => {
    history.push(toLink, {tasks: userTasks});
  };

  const TaskConclusionSummaryLabel = () => {
    const areAllTasksHandled = userTasks?.every((task) => task.careTask.markedAsHandled);

    if (areAllTasksHandled) {
      const areAsDone = userTasks?.every((task) => task.careTask.markedAsDone);

      return (
        <Typography
          sx={{
            ...taskConclusionSx,
            color: areAsDone ? 'var(--dcm-green-base)' : 'var(--dcm-red-base)',
          }}
        >
          {t(translations.daily_care_log.cards.entity_user.task_conclusion_summary_label.concluded)}
        </Typography>
      );
    } else {
      const unhandledTasks = userTasks?.filter((task) => !task.careTask.markedAsHandled);

      const hasSomeMissedTasks = userTasks?.some((task) =>
        moment(task.careTask.startsAt).isBefore(moment())
      );

      return (
        <Typography
          sx={{
            ...taskConclusionSx,
            color: hasSomeMissedTasks ? 'var(--dcm-yellow-base)' : 'var(--dcm-gray)',
          }}
        >
          {unhandledTasks?.length}{' '}
          {(
            t(
              translations.daily_care_log.cards.entity_user.task_conclusion_summary_label.task
            ) as string
          ).toLowerCase()}
          {unhandledTasks?.length > 1 ? 's' : ''}
        </Typography>
      );
    }
  };

  return (
    <Card sx={cardSx} onClick={handleCardClick}>
      <CardContent sx={cardContentSx}>
        <Stack gap="14px" justifyContent="center" alignItems="center">
          <Avatar
            src={data.user.picture}
            sx={{
              ...avatarSx,
              border: data.entitySocialResponses[0]?.foregroundColor
                ? `2px solid ${data.entitySocialResponses[0]?.foregroundColor}`
                : 'unset',
            }}
          />
          <Typography sx={usernameSx} align="center" noWrap>
            {data.user.displayName ?? data.user.name}
          </Typography>
          {Array.isArray(userTasks) && userTasks.length > 0 && <TaskConclusionSummaryLabel />}
        </Stack>
      </CardContent>
    </Card>
  );
};
